import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Error debouncing to prevent duplicate toasts
let lastErrorTime = 0;
let lastErrorType = "";
const ERROR_DEBOUNCE_MS = 3000;

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: API_BASE,
});

// Add auth header to all requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for centralized error handling.
//
// NOTE: 401 auth handling is intentionally delegated to the global axios
// interceptors installed by `utils/authTokenManager.installAuthInterceptors()`
// — which perform silent refresh-token rotation and only end the session
// if the refresh itself fails. Clearing tokens here on the first 401 would
// short-circuit that flow and force users to re-login on every access-token
// expiry. So this interceptor only handles network-level errors, and lets
// 401s bubble up to the global handler.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const now = Date.now();

    // Handle network errors (CORS, server down, etc.)
    if (!error.response) {
      // Debounce network errors
      if (now - lastErrorTime > ERROR_DEBOUNCE_MS || lastErrorType !== "network") {
        lastErrorTime = now;
        lastErrorType = "network";
        console.error("Network error:", error.message);
        // Don't show toast for network errors during initial load
      }
      return Promise.reject(error);
    }

    // For all other errors (including 401), pass through — the global
    // authTokenManager interceptor handles silent refresh + retry, then
    // hard logout only if the refresh token itself is dead.
    return Promise.reject(error);
  }
);

// Helper to get auth config for manual axios calls
export const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

// For cookie-based auth (Google OAuth)
export const getAuthConfigWithCredentials = () => {
  const token = localStorage.getItem("token");
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

// Helper for showing error toasts with proper handling
export const showErrorToast = (error, defaultMessage = "An error occurred") => {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string") {
    toast.error(detail);
  } else if (Array.isArray(detail)) {
    toast.error(detail.map(e => e.msg || String(e)).join(", "));
  } else if (typeof detail === "object" && detail.msg) {
    toast.error(detail.msg);
  } else {
    toast.error(defaultMessage);
  }
};

export default apiClient;
